// BUILD-042 — Unified employee workspace. Shared initials-avatar helper, extracted from
// EmployeeCard.tsx so My Team's roster rows (and any future list of people) can show the same
// consistent avatar treatment instead of a bare name with no visual identity. A roster of
// several different people all showing the identical blue circle reads as monotonous — this
// assigns one of a small fixed palette deterministically from the employee's id/name, so the
// SAME person always gets the SAME color across renders/pages, but different people visually
// stand apart from each other, matching the approved reference's distinct avatar-color
// convention (see preview/data.ts's own `photoColor` field for the same idea, applied here to
// the REAL roster instead of synthetic data).
const PALETTE = ['#4AABDE', '#6FA82C', '#C4752B', '#8B5CF6', '#DB4C83', '#2E9E8E'];

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export function colorFor(key: string): string {
  return PALETTE[hashString(key) % PALETTE.length];
}

// Role-driven avatar colour (2026-10-13 Owner request). The circle (and, once a picture is
// uploaded, the ring around it) is coloured by the person's Unique HQ role so the roster reads
// by role at a glance. Owner and Admin share one colour; the rest are distinct; anyone with no
// recognised role falls back to neutral gray. Keyed on the raw Cognito group id.
const ROLE_COLORS: Record<string, string> = {
  owner: '#DC2626',          // red
  admin: '#DC2626',          // red (same as owner, per Owner request)
  designer: '#2563EB',       // blue
  pm: '#16A34A',             // green
  warranty: '#8B5CF6',       // purple
  superintendent: '#C4752B', // orange
  accounting: '#2E9E8E',     // teal
};
const ROLE_COLOR_FALLBACK = '#6B7280'; // gray — no recognised role

// Picks a colour from the person's role groups. If they hold more than one group, the highest-
// authority recognised role wins (owner → admin → the rest), so a multi-role person has a
// stable, meaningful colour rather than one that depends on group ordering.
const ROLE_PRIORITY = ['owner', 'admin', 'designer', 'pm', 'warranty', 'superintendent', 'accounting'];
export function roleColorFor(groups: string[] | null | undefined): string {
  const g = groups ?? [];
  for (const role of ROLE_PRIORITY) {
    if (g.includes(role) && ROLE_COLORS[role]) return ROLE_COLORS[role];
  }
  return ROLE_COLOR_FALLBACK;
}

export function initialsFrom(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function Avatar({
  name, colorKey, sizeClass = 'w-10 h-10', textClass = 'text-sm', color, imageUrl,
}: {
  name: string;
  colorKey: string;
  sizeClass?: string;
  textClass?: string;
  /** Explicit circle colour. When omitted, a stable colour is derived from colorKey (legacy
   *  per-person palette). Pass this to drive the colour from something meaningful, e.g. role. */
  color?: string;
  /** When set, the picture fills the circle and `color` becomes a ring around it. Until an
   *  upload exists this is undefined, so the circle shows initials in `color`. */
  imageUrl?: string | null;
}) {
  const resolved = color || colorFor(colorKey);
  // The picture URL ends up inside a CSS url("...") value, so only a plain https: URL or an inline
  // data:image/ URL is ever used; anything else (javascript:, quotes, parentheses, whitespace that
  // could break out of the value) falls back to initials. No caller passes imageUrl yet — this keeps
  // a future upload feature from turning a stored string into a style-injection point.
  const safeImageUrl = imageUrl && /^(https:\/\/|data:image\/(png|jpe?g|webp|gif);base64,)[A-Za-z0-9\-._~:/?#[\]@!$&'*+,;=%]+$/.test(imageUrl)
    && !/["'()\\\s]/.test(imageUrl)
    ? imageUrl
    : null;
  if (safeImageUrl) {
    return (
      <div
        className={`${sizeClass} rounded-full flex-shrink-0 bg-center bg-cover ring-2`}
        style={{ backgroundImage: `url("${safeImageUrl}")`, ['--tw-ring-color' as any]: resolved }}
        role="img"
        aria-label={name}
      />
    );
  }
  return (
    <div
      className={`${sizeClass} rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 ${textClass}`}
      style={{ backgroundColor: resolved }}
      aria-hidden="true"
    >
      {initialsFrom(name)}
    </div>
  );
}
